import * as React from "react";
import { useHistory } from "react-router-dom";
import { useMsal } from "@azure/msal-react";

import { usePromise } from "common/hooks/use-promise";
import {
  deleteTokenCookie,
  setTokenCookie,
} from "common/context/auth/token-cookie";
import { FullPageSpinner } from "common/components/full-page/full-page-spinner";
import type {
  UserInfo,
  UserInfoResponse,
} from "common/context/auth/token-validation";
import { tokenValidation } from "common/context/auth/token-validation";
import { getAuthenticationType } from "common/context/auth/get-authentication-type";
import { FullPageErrorFallback } from "common/components/full-page/full-page-error-fallback";
import type { SubmitLoginParams } from "common/context/auth/submit-login-request";
import { submitLoginRequest } from "common/context/auth/submit-login-request";
import { MSALLoginPopup } from "common/context/auth/msal/msal-login-popup";
import isMSLegacy from "common/utils/is-ms-legacy";
import { createIDBStores } from "common/utils/storage/idb";
import idbStores from "common/utils/storage/idb-stores";
import { APP_VERSION_ERROR } from "common/constants/config-error-messages.constants";
import { setUser, setUserId } from "common/utils/user-data";
import { MSALAcquireTokenPopup } from "common/context/auth/msal/msal-acquire-token-popup";
import { msalLoginRequest } from "common/context/auth/msal/msal-login-request";
import type { Result } from "common/utils/result";

type LoginType = ({
  userId,
  password,
  msalResponse,
}: SubmitLoginParams) => Promise<Result<null>>;

type AuthContextType =
  | {
      user: UserInfo;
      submitUserId: typeof getAuthenticationType;
      login: LoginType;
      MSALLogin: (userId: string) => ReturnType<LoginType>;
      MSALTokenLogin: () => Promise<Result<null>>;
      logout: () => void;
    }
  | undefined;
const AuthContext = React.createContext<AuthContextType>(undefined);
AuthContext.displayName = "AuthContext";

async function setDataAfterLogin(
  userInfoResponse: UserInfoResponse,
  setData: (data: UserInfo) => void,
): Promise<Result<null>> {
  // TODO: remove idb
  if (isMSLegacy() === false) {
    const error = await createIDBStores(idbStores);
    if (error instanceof Error) {
      return new Error(
        `${error}. ${APP_VERSION_ERROR.errorTitle}. ${APP_VERSION_ERROR.errorMessage}`,
      );
    }
  }

  const { id, name, email, roles, token } = userInfoResponse;

  // TODO: remove these two lines
  setUserId(id);
  setUser({ name, email });

  setTokenCookie(token);

  setData({
    id,
    name,
    email,
    roles,
  });

  return null;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { instance: MSALInstance } = useMsal();
  const history = useHistory();

  const { promiseState, run, setData } = usePromise<UserInfo>();

  React.useEffect(() => {
    const userPromise = tokenValidation();
    run(userPromise);
  }, [run]);

  const submitUserId = React.useCallback(
    (userId: string) => getAuthenticationType(userId),
    [],
  );

  const login = React.useCallback(
    async ({
      userId,
      password = "",
      msalResponse,
    }: SubmitLoginParams): Promise<Result<null>> => {
      const loginResult = await submitLoginRequest({
        userId,
        password,
        msalResponse,
      });
      if (loginResult instanceof Error) {
        return loginResult;
      }

      const setDataResult = await setDataAfterLogin(loginResult, setData);
      if (setDataResult instanceof Error) {
        return setDataResult;
      }

      return null;
    },
    [setData],
  );

  const MSALLogin = React.useCallback(
    async (userId: string) => {
      const msalResult = await MSALLoginPopup(MSALInstance, userId);
      if (msalResult instanceof Error) {
        return msalResult;
      }

      return login({ userId, msalResponse: JSON.stringify(msalResult) });
    },
    [MSALInstance, login],
  );

  const MSALTokenLogin = React.useCallback(async () => {
    const msalTokenResult = await MSALAcquireTokenPopup(MSALInstance);
    if (msalTokenResult instanceof Error) {
      return msalTokenResult;
    }

    const msalLoginResult = await msalLoginRequest(
      JSON.stringify(msalTokenResult),
    );
    if (msalLoginResult instanceof Error) {
      return msalLoginResult;
    }

    const setDataResult = await setDataAfterLogin(msalLoginResult, setData);
    if (setDataResult instanceof Error) {
      return setDataResult;
    }

    return null;
  }, [MSALInstance, setData]);

  const logout = React.useCallback(() => {
    deleteTokenCookie();

    MSALInstance.setActiveAccount(null);

    setData(null);
    history.push("/");
  }, [MSALInstance, history, setData]);

  const value = React.useMemo(
    () => ({
      user: promiseState.status === "resolved" ? promiseState.data : null,
      submitUserId,
      login,
      MSALLogin,
      MSALTokenLogin,
      logout,
    }),
    [promiseState, submitUserId, login, MSALLogin, MSALTokenLogin, logout],
  );

  if (promiseState.status === "pending" || promiseState.status === "idle") {
    return <FullPageSpinner />;
  }

  if (promiseState.status === "rejected") {
    return <FullPageErrorFallback message={promiseState.error.message} />;
  }

  if (promiseState.status === "resolved") {
    return (
      <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
    );
  }

  return (
    <div>If this message is displayed there is an unhandled promise status</div>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }

  return context;
}
